/**
 * scene-trainable.ts — /train 决策训练池准入谓词(读时派生)
 *
 * 背景(B 类硬伤):/train 决策训练器曾混入 parsed=false 的「概念题」
 * (scene-055/165/170 类——问「开局该怎么做 / 最好的牌 / 范围宽不宽」),
 * 这类题没有可判分的行动选项,却被渲染成过牌/下注尺度按钮,题目≠按钮,新手 3/3 选不出。
 *
 * 本谓词是 /train 决策池的唯一准入判据,与首页选景同口径(page.tsx:24):
 *   decision.parsed === true && decision.options.length >= 2
 * parsed=false 的概念题一律不进 /train 决策训练器(概念题该排除,而非伪装成决策题)。
 *
 * 纯前端派生:只读已有的 scene.decision(decision-parser 的结构化输出),
 * 不调 LLM、不碰任何数据文件、不修改 scene 对象。供 queryScenes(trainOnly) 与
 * /train 选景处共用,保证查询层与渲染层同一判据。
 */

import type { StructuredDecision } from "./decision-parser";

/**
 * CONTENT_V2 开关的**客户端安全**读取(逻辑与 content-v2.ts 的 isContentV2 逐字一致)。
 * **默认开(常态化)**,仅显式 `CONTENT_V2 === "0"` 回落 v1(2026-08-03 释放翻转,与
 * content-v2.ts 同步)。**不能** `import { isContentV2 } from "./content-v2"`:content-v2.ts
 * 顶层 import node:fs,而本文件被 train/page.tsx(客户端组件)import,会把 node:fs 拽进
 * 客户端 bundle,Turbopack 直接 Build Error 击穿 /train(2026-08-02 翻闸引入的回归,修复于此)。
 * 故就地内联同一 env 判断,切断对 content-v2 模块的依赖。服务端 queries.ts 仍从
 * content-v2 导入 isContentV2(服务端可用 node:fs),两处行为一致。
 */
function isContentV2(): boolean {
  return process.env.CONTENT_V2 !== "0";
}

/** 准入判据只需决策结构;PilotScene 天然满足(结构子集,便于测试构造样本)。 */
export type TrainableInput = {
  decision?: StructuredDecision | null;
};

/**
 * 该场景能否进 /train 决策训练器。
 * - parsed === true:自由文本已成功拆成结构化决策(narrative/ambiguous/无选项 → false)。
 * - options.length >= 2:至少两个行动按钮可选(单选项不构成决策)。
 * 二者同真才准入;概念题(parsed=false)与缺选项场景一律排除。
 */
export function isTrainableDecision(scene: TrainableInput): boolean {
  const d = scene.decision;
  return d?.parsed === true && d.options.length >= 2;
}

/**
 * 分层放行闸门(release-layer gate)——当前放行哪些 spot_source 层进 /train。
 *
 * **放行依据 = 非劣性判据(Oracle 裁决 2026-08-02,全量放行 parsed + generated)**:
 *   ① generated 层多采样复验 P1 看懂 100% 零方差(磨前 60% → +40pp);
 *   ② 看懂三维 96.7 / 96.7 / 100,历史首过;
 *   ③ 分层指标非劣于已放行 parsed 层(5 胜 3 平 3 负,且 3 负全在噪声内)。
 *   Oracle 两条证据链:闸门双标——当前对 generated 失败的绝对阈项,在已放行 parsed 层
 *   同样失败(parsed 当初仅凭「P1 看懂 ≥85%」单条放行,见本文件 L38-40 历史口径),
 *   13 项绝对阈从未对 parsed 执行、只对 generated 执行 = 双标;P3 urge 残差是 persona
 *   prompt 写死偏置(P3「退出冲动天生比别人高/稍有卡顿就给高分」),与 P1/P2 不同量纲,
 *   拿同 ≤4 比是测量设计错误而非产品缺陷。判别器:P1/P2 同屏能选 100% → 按钮映射无缺陷,
 *   P3 残差纯 GTO 内容难度。新闸门下 generated 全项通过 → 全量放行。
 *
 * **判据性质:这是放量判据,非永久放宽的绝对阈。** 最终 release gate = Stage4 真人验收
 *   (5 秒看懂率 ≥80% / 15 秒出手率 ≥90% / 续玩率 ≥70%,正确率不设门)。
 *
 * **可逆翻闸**:若 Stage4 真人验收或后续复验回退,只需
 *   `RELEASED_SPOT_SOURCES.delete("generated")`(或把初始化集合改回 new Set(["parsed"]))
 *   即收回 generated 层,无需改任何消费端——queries.ts(trainOnly)与 /train page.tsx 的
 *   5 处选景都经 isTrainPoolScene 收口。
 *
 * **CONTENT_V2 条件化防回归**:本闸门仅在 v2 体制(CONTENT_V2=1)经 isTrainPoolScene 的
 *   第 3 层门生效;CONTENT_V2 关时层门透传(见 isTrainPoolScene),不回归旧 /train 行为。
 *
 * 语义独立于 needs_review:needs_review = 「这道题闸门失败(牌面矛盾)」的内容质量信号;
 * 本闸门 = 「这一层整体放行与否」的分层放量信号。二者正交,不互相替代。
 */
export const RELEASED_SPOT_SOURCES: ReadonlySet<string> = new Set(["parsed", "generated"]);

/**
 * 该 spot 所在层是否已放行进 /train。
 * spot.spot_source ∈ RELEASED_SPOT_SOURCES 才放行;spot_source 缺失 → 保守不放行(false)。
 * 保守取向:未知来源宁可不进学习者决策池(质量 > 数量),来源补齐/翻闸后自然恢复。
 */
export function isReleasedLayer(
  spot: { spot_source?: string } | null | undefined,
): boolean {
  const src = spot?.spot_source;
  return typeof src === "string" && RELEASED_SPOT_SOURCES.has(src);
}

/**
 * /train 决策池准入(三层门):
 *   1. 决策结构  isTrainableDecision(parsed === true && options.length >= 2)
 *   2. 内容质量  未被标 needs_review(重生成仍矛盾的标审场景,旧文案有牌面矛盾,
 *                不进学习者决策池;质量 > 数量,同 /quiz 排除范式 quiz/page.tsx:641)
 *   3. 分层放行  isReleasedLayer(spot)(parsed + generated 层均放行;Oracle 裁决 2026-08-02)
 *
 * 第 3 层门是 **v2 体制专属**:`!isContentV2() || isReleasedLayer(spot)`。
 *   - CONTENT_V2 关(旧体制):spot_source 根本不存在(scenes_index 全缺失),无 parsed/generated
 *     分层概念 → 层门透传放行,维持旧 /train 行为,**不清空 /train**(防回归)。
 *   - CONTENT_V2=1(v2 体制):spot_source 由 scene_copy_v2.json 合并进 spot → 层门生效,
 *     parsed + generated 层均放行(放行集 RELEASED_SPOT_SOURCES 收口)。
 * 前两层门(决策结构 + 内容质量)与体制无关,恒生效。任一层挂即排除。
 */
export function isTrainPoolScene(scene: {
  decision?: StructuredDecision | null;
  spot?: { needs_review?: boolean; spot_source?: string } | null;
}): boolean {
  return (
    isTrainableDecision(scene) &&
    !scene.spot?.needs_review &&
    (!isContentV2() || isReleasedLayer(scene.spot))
  );
}
